// Generated by scripts/notification_catalog/generate_notification_catalog.py — do not edit.
// Source of truth: libraries/python/notification_catalog and notification_preferences.

import { type NotificationChannel } from '@apps/web/lib/notification_channel_features';

export type NotificationTypeKey =
    | 'agent_reminder'
    | 'callout_shift_accepted'
    | 'outreach_finished_unfilled'
    | 'outreach_finished_filled'
    | 'callout_shift_needs_review'
    | 'missed_clock_in'
    | 'timely_clock_in'
    | 'missed_clock_out'
    | 'timely_clock_out'
    | 'phoebe_clocked_in_out'
    | 'caregiver_running_late'
    | 'clock_in_out'
    | 'shift_confirmation_no_response'
    | 'shift_confirmation_needs_review'
    | 'shift_confirmation_staged_messages_reminder'
    | 'plan_of_care_incomplete'
    | 'shift_confirmation_declined'
    | 'shift_cancellation'
    | 'shift_confirmation_confirmed'
    | 'after_hours_receptionist_urgent'
    | 'call_summary'
    | 'after_hours_receptionist_digest'
    | 'inbound_sms_received'
    | 'conversation_assigned'
    | 'conversation_tagged'
    | 'task_assigned'
    | 'activity_action_required'
    | 'activity_urgent'
    | 'activity_daily_summary';

export type ToastNotificationTypeKey =
    | 'settings_update'
    | 'outreach_action'
    | 'app_feedback'
    | 'form_failure'
    | 'message_action'
    | 'shift_action'
    | 'data_sync_action';

export type NotificationVisibility =
    | 'mandatory'
    | 'producer'
    | 'retired'
    | 'system'
    | 'user';

export type NotificationPriority = 'important' | 'routine' | 'urgent';

export type NotificationSettingsSection =
    | 'How Phoebe asks for help'
    | 'Filling shifts'
    | 'Shift confirmation'
    | 'Arriving at shifts'
    | 'Canceling shifts'
    | 'Calls'
    | 'Messages'
    | 'Activity';

export type NotificationSettingsArea =
    | 'How does Phoebe ask for help?'
    | 'Scheduling and operations'
    | 'Communication'
    | 'Activity';

export type NotificationWebCategory =
    | 'alerts'
    | 'messages'
    | 'outreach'
    | 'shifts';

// Global values changed by a per-channel reset. Keep these next
// to the generated event defaults so the client cannot fork the
// backend's definition of a new account.
export const NOTIFICATION_CHANNEL_RESET_DEFAULTS = {
    email: {
        activityActionRequiredEmailMinPriority: 'low',
        globalEnabled: true,
    },
    inapp: {
        browserEnabled: false,
        soundEnabled: true,
        toastGlobalEnabled: true,
    },
} as const;

// Types whose catalog policy bypasses every user schedule.
export const ALWAYS_DELIVER_NOTIFICATION_TYPES: ReadonlyArray<string> = [
    'global_enabled',
    'sound_enabled',
    'browser_enabled',
    'settings_update',
    'emergency_call_placed',
];

export type NotificationCatalogEntry = {
    readonly defaultChannels: Readonly<Record<NotificationChannel, boolean>>;
    readonly defaultToastEnabled: boolean;
    readonly eventLabel: string;
    readonly helperText: string | null;
    readonly label: string;
    readonly priority: NotificationPriority;
    readonly settingsGroupKey: NotificationTypeKey | null;
    readonly settingsSection: NotificationSettingsSection | null;
    readonly supportedChannels: ReadonlyArray<NotificationChannel>;
    readonly visibility: NotificationVisibility;
    readonly webCategory: NotificationWebCategory | null;
};

export const NOTIFICATION_CATALOG: Readonly<
    Record<NotificationTypeKey, NotificationCatalogEntry>
> = {
    agent_reminder: {
        defaultChannels: {
            email: false,
            inapp: true,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Agent reminder',
        helperText:
            'A Phoebe chat follows up with a reminder you asked for or something that needs you.',
        label: 'Agent reminders',
        priority: 'routine',
        settingsGroupKey: 'agent_reminder',
        settingsSection: 'How Phoebe asks for help',
        supportedChannels: ['inapp'],
        visibility: 'user',
        webCategory: 'alerts',
    },
    callout_shift_accepted: {
        defaultChannels: {
            email: true,
            inapp: true,
            sms: true,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Shift accepted',
        helperText: 'Caregiver accepts an open shift you are tracking.',
        label: 'Shift accepted',
        priority: 'routine',
        settingsGroupKey: 'callout_shift_accepted',
        settingsSection: 'Filling shifts',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'outreach',
    },
    outreach_finished_unfilled: {
        defaultChannels: {
            email: true,
            inapp: true,
            sms: true,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Outreach unfilled',
        helperText:
            'An open shift is still unfilled shortly before it starts, or its outreach ends with no caregiver found.',
        label: 'Outreach finished unfilled',
        priority: 'important',
        settingsGroupKey: 'outreach_finished_unfilled',
        settingsSection: 'Filling shifts',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'outreach',
    },
    outreach_finished_filled: {
        defaultChannels: {
            email: true,
            inapp: true,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Outreach filled',
        helperText: 'An open shift is filled through Phoebe.',
        label: 'Outreach finished filled',
        priority: 'routine',
        settingsGroupKey: 'outreach_finished_filled',
        settingsSection: 'Filling shifts',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'outreach',
    },
    callout_shift_needs_review: {
        defaultChannels: {
            email: true,
            inapp: true,
            sms: true,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Outreach needs review',
        helperText: 'A caregiver responds with conditional availability.',
        label: 'Open shift response needs review',
        priority: 'important',
        settingsGroupKey: 'callout_shift_needs_review',
        settingsSection: 'Filling shifts',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'outreach',
    },
    missed_clock_in: {
        defaultChannels: {
            email: false,
            inapp: true,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Missed clock-in after reminders',
        helperText:
            'A caregiver does not clock in after reminders, or Phoebe receives the clock-in but cannot record it in the EHR.',
        label: 'Missed clock-in',
        priority: 'urgent',
        settingsGroupKey: 'missed_clock_in',
        settingsSection: 'Arriving at shifts',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'outreach',
    },
    timely_clock_in: {
        defaultChannels: {
            email: false,
            inapp: true,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Clock-in writeback needs review',
        helperText: null,
        label: 'Missed clock-in',
        priority: 'urgent',
        settingsGroupKey: 'missed_clock_in',
        settingsSection: null,
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'outreach',
    },
    missed_clock_out: {
        defaultChannels: {
            email: false,
            inapp: true,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Missed clock-out after reminders',
        helperText:
            'A caregiver does not clock out after reminders, or Phoebe receives the clock-out but cannot record it in the EHR.',
        label: 'Missed clock-out',
        priority: 'urgent',
        settingsGroupKey: 'missed_clock_out',
        settingsSection: 'Arriving at shifts',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'outreach',
    },
    timely_clock_out: {
        defaultChannels: {
            email: false,
            inapp: true,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Clock-out writeback needs review',
        helperText: null,
        label: 'Missed clock-out',
        priority: 'urgent',
        settingsGroupKey: 'missed_clock_out',
        settingsSection: null,
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'outreach',
    },
    phoebe_clocked_in_out: {
        defaultChannels: {
            email: false,
            inapp: false,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: false,
        eventLabel: 'Phoebe recorded a clock',
        helperText:
            "Phoebe records a caregiver's clock-in or clock-out for them by text or call. Clocks entered directly in the EHR are not included.",
        label: 'Phoebe clocked in or out',
        priority: 'routine',
        settingsGroupKey: 'phoebe_clocked_in_out',
        settingsSection: 'Arriving at shifts',
        supportedChannels: ['inapp', 'sms'],
        visibility: 'user',
        webCategory: 'shifts',
    },
    caregiver_running_late: {
        defaultChannels: {
            email: false,
            inapp: true,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Caregiver running late',
        helperText:
            "A caregiver tells Phoebe by text or call that they'll be late, or gives a new arrival time.",
        label: 'Caregiver running late',
        priority: 'important',
        settingsGroupKey: 'caregiver_running_late',
        settingsSection: 'Arriving at shifts',
        supportedChannels: ['inapp', 'sms'],
        visibility: 'user',
        webCategory: 'shifts',
    },
    clock_in_out: {
        defaultChannels: {
            email: false,
            inapp: true,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Clock in/out',
        helperText: null,
        label: 'Clock in/out',
        priority: 'routine',
        settingsGroupKey: 'clock_in_out',
        settingsSection: null,
        supportedChannels: [],
        visibility: 'retired',
        webCategory: 'outreach',
    },
    shift_confirmation_no_response: {
        defaultChannels: {
            email: false,
            inapp: false,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: false,
        eventLabel: 'No response',
        helperText:
            'No caregiver reply after the final follow-up and the wait set in shift confirmation settings.',
        label: 'No response',
        priority: 'urgent',
        settingsGroupKey: 'shift_confirmation_no_response',
        settingsSection: 'Arriving at shifts',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'shifts',
    },
    shift_confirmation_needs_review: {
        defaultChannels: {
            email: true,
            inapp: true,
            sms: true,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Shift needs review',
        helperText: 'A shift confirmation response needs manual review.',
        label: 'Needs review',
        priority: 'important',
        settingsGroupKey: 'shift_confirmation_needs_review',
        settingsSection: 'Arriving at shifts',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'shifts',
    },
    shift_confirmation_staged_messages_reminder: {
        defaultChannels: {
            email: false,
            inapp: false,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: false,
        eventLabel: 'Draft needs review',
        helperText: 'Reminder when there are new pending messages.',
        label: 'Pending messages reminder',
        priority: 'important',
        settingsGroupKey: 'shift_confirmation_staged_messages_reminder',
        settingsSection: 'Arriving at shifts',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'shifts',
    },
    plan_of_care_incomplete: {
        defaultChannels: {
            email: false,
            inapp: true,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Incomplete plan-of-care tasks',
        helperText: 'A completed visit has no recorded plan-of-care tasks.',
        label: 'Incomplete plan-of-care tasks',
        priority: 'important',
        settingsGroupKey: 'plan_of_care_incomplete',
        settingsSection: 'Arriving at shifts',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'alerts',
    },
    shift_confirmation_declined: {
        defaultChannels: {
            email: true,
            inapp: true,
            sms: true,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Shift declined',
        helperText: 'A caregiver declines a shift confirmation.',
        label: 'Shift declined',
        priority: 'important',
        settingsGroupKey: 'shift_confirmation_declined',
        settingsSection: 'Canceling shifts',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'shifts',
    },
    shift_cancellation: {
        defaultChannels: {
            email: true,
            inapp: true,
            sms: true,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Call out',
        helperText:
            'A caregiver cancels an assigned shift by text or phone, or the cancellation needs your attention.',
        label: 'Call out',
        priority: 'important',
        settingsGroupKey: 'shift_cancellation',
        settingsSection: 'Canceling shifts',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'shifts',
    },
    shift_confirmation_confirmed: {
        defaultChannels: {
            email: false,
            inapp: true,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Shift confirmed',
        helperText: 'A caregiver confirms their shift.',
        label: 'Shift confirmed',
        priority: 'routine',
        settingsGroupKey: 'shift_confirmation_confirmed',
        settingsSection: 'Shift confirmation',
        supportedChannels: ['inapp'],
        visibility: 'user',
        webCategory: 'shifts',
    },
    after_hours_receptionist_urgent: {
        defaultChannels: {
            email: true,
            inapp: true,
            sms: true,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Urgent call',
        helperText: 'Phoebe captures an urgent call.',
        label: 'Urgent call',
        priority: 'urgent',
        settingsGroupKey: 'after_hours_receptionist_urgent',
        settingsSection: 'Calls',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'alerts',
    },
    call_summary: {
        defaultChannels: {
            email: false,
            inapp: false,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: false,
        eventLabel: 'Call summary',
        helperText:
            'A recap after Phoebe answers an incoming call, including calls she takes a message on. Calls a person picks up, and calls covered by urgent call alerts, are not repeated here.',
        label: 'Summary of every call Phoebe answers',
        priority: 'routine',
        settingsGroupKey: 'call_summary',
        settingsSection: 'Calls',
        supportedChannels: ['inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'alerts',
    },
    after_hours_receptionist_digest: {
        defaultChannels: {
            email: false,
            inapp: false,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: false,
        eventLabel: 'Recap of calls',
        helperText: 'Summary of calls Phoebe handled.',
        label: 'Daily digest email',
        priority: 'routine',
        settingsGroupKey: 'after_hours_receptionist_digest',
        settingsSection: null,
        supportedChannels: ['email'],
        visibility: 'user',
        webCategory: 'alerts',
    },
    inbound_sms_received: {
        defaultChannels: {
            email: false,
            inapp: false,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: false,
        eventLabel: 'Incoming text',
        helperText: 'Someone sends Phoebe a text message.',
        label: 'Incoming texts',
        priority: 'routine',
        settingsGroupKey: 'inbound_sms_received',
        settingsSection: 'Messages',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'messages',
    },
    conversation_assigned: {
        defaultChannels: {
            email: true,
            inapp: true,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Conversation assigned',
        helperText: 'Someone assigns a conversation to you.',
        label: 'Conversation assigned',
        priority: 'important',
        settingsGroupKey: 'conversation_assigned',
        settingsSection: 'Messages',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'messages',
    },
    conversation_tagged: {
        defaultChannels: {
            email: true,
            inapp: true,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Tagged in an internal thread',
        helperText: 'A teammate tags you in an internal note.',
        label: 'Tagged in an internal thread',
        priority: 'important',
        settingsGroupKey: 'conversation_tagged',
        settingsSection: 'Messages',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'messages',
    },
    task_assigned: {
        defaultChannels: {
            email: true,
            inapp: true,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Task assigned',
        helperText: 'Someone assigns an Inbox task to you.',
        label: 'Task assigned',
        priority: 'important',
        settingsGroupKey: 'task_assigned',
        settingsSection: 'Activity',
        supportedChannels: ['email', 'inapp'],
        visibility: 'user',
        webCategory: 'alerts',
    },
    activity_action_required: {
        defaultChannels: {
            email: true,
            inapp: true,
            sms: true,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Action required',
        helperText: 'An activity feed item is marked as needing action.',
        label: 'Activity needs action',
        priority: 'important',
        settingsGroupKey: 'activity_action_required',
        settingsSection: 'Activity',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'alerts',
    },
    activity_urgent: {
        defaultChannels: {
            email: true,
            inapp: true,
            sms: true,
            voice: false,
        },
        defaultToastEnabled: true,
        eventLabel: 'Urgent activity',
        helperText:
            'An urgent activity feed item appears, even if it is informational.',
        label: 'Urgent activity',
        priority: 'urgent',
        settingsGroupKey: 'activity_urgent',
        settingsSection: 'Activity',
        supportedChannels: ['email', 'inapp', 'sms', 'voice'],
        visibility: 'user',
        webCategory: 'alerts',
    },
    activity_daily_summary: {
        defaultChannels: {
            email: true,
            inapp: false,
            sms: false,
            voice: false,
        },
        defaultToastEnabled: false,
        eventLabel: 'Daily activity summary',
        helperText: "One email each morning recapping yesterday's activity.",
        label: 'Daily activity summary',
        priority: 'routine',
        settingsGroupKey: 'activity_daily_summary',
        settingsSection: 'Activity',
        supportedChannels: ['email'],
        visibility: 'user',
        webCategory: 'alerts',
    },
};

// Every key normal settings or a reset may write. A retired key
// keeps its catalog entry so stored rows stay readable, but nothing
// new should write it.
export const NOTIFICATION_TYPE_KEYS: ReadonlyArray<NotificationTypeKey> = [
    'agent_reminder',
    'callout_shift_accepted',
    'outreach_finished_unfilled',
    'outreach_finished_filled',
    'callout_shift_needs_review',
    'missed_clock_in',
    'timely_clock_in',
    'missed_clock_out',
    'timely_clock_out',
    'phoebe_clocked_in_out',
    'caregiver_running_late',
    'shift_confirmation_no_response',
    'shift_confirmation_needs_review',
    'shift_confirmation_staged_messages_reminder',
    'plan_of_care_incomplete',
    'shift_confirmation_declined',
    'shift_cancellation',
    'shift_confirmation_confirmed',
    'after_hours_receptionist_urgent',
    'call_summary',
    'after_hours_receptionist_digest',
    'inbound_sms_received',
    'conversation_assigned',
    'conversation_tagged',
    'task_assigned',
    'activity_action_required',
    'activity_urgent',
    'activity_daily_summary',
];

// Toast keys the frontend may raise itself. The server allowlist is
// separate and currently empty.
export const TOAST_NOTIFICATION_TYPE_KEYS: ReadonlyArray<ToastNotificationTypeKey> =
    [
        'settings_update',
        'outreach_action',
        'app_feedback',
        'form_failure',
        'message_action',
        'shift_action',
        'data_sync_action',
    ];

// What actually happened, for bell rows, toasts, and message copy.
// A settings row may combine two types under one label; the
// delivered alert still names the exact event.
export const NOTIFICATION_EVENT_LABELS: Readonly<Record<string, string>> = {
    global_enabled: 'All email notifications',
    sound_enabled: 'Notification sound',
    browser_enabled: 'Browser notifications',
    settings_update: 'Notification settings changed',
    agent_reminder: 'Agent reminder',
    callout_shift_accepted: 'Shift accepted',
    outreach_finished_unfilled: 'Outreach unfilled',
    outreach_finished_filled: 'Outreach filled',
    callout_shift_needs_review: 'Outreach needs review',
    missed_clock_in: 'Missed clock-in after reminders',
    timely_clock_in: 'Clock-in writeback needs review',
    missed_clock_out: 'Missed clock-out after reminders',
    timely_clock_out: 'Clock-out writeback needs review',
    phoebe_clocked_in_out: 'Phoebe recorded a clock',
    caregiver_running_late: 'Caregiver running late',
    clock_in_out: 'Clock in/out',
    shift_confirmation_no_response: 'No response',
    shift_confirmation_needs_review: 'Shift needs review',
    shift_confirmation_staged_messages_reminder: 'Draft needs review',
    plan_of_care_incomplete: 'Incomplete plan-of-care tasks',
    shift_confirmation_declined: 'Shift declined',
    shift_cancellation: 'Call out',
    shift_confirmation_confirmed: 'Shift confirmed',
    after_hours_receptionist_urgent: 'Urgent call',
    call_summary: 'Call summary',
    after_hours_receptionist_digest: 'Recap of calls',
    emergency_call_placed: 'Emergency call placed',
    inbound_sms_received: 'Incoming text',
    conversation_assigned: 'Conversation assigned',
    conversation_tagged: 'Tagged in an internal thread',
    task_assigned: 'Task assigned',
    activity_action_required: 'Action required',
    activity_urgent: 'Urgent activity',
    activity_daily_summary: 'Daily activity summary',
    outreach_action: 'Outreach action',
    app_feedback: 'App feedback',
    form_failure: 'Form failure',
    message_action: 'Message action',
    shift_action: 'Shift action',
    data_sync_action: 'Data sync action',
    phoebe_created_outreach: 'Phoebe created outreach',
    phoebe_started_outreach: 'Phoebe started outreach',
    phoebe_monitored_outreach: 'Phoebe monitored outreach',
    phoebe_acted_on_outreach: 'Phoebe acted on outreach',
};

// Settings rows that control more than one type, keyed by every
// member. A toggle, reset, or unsubscribe that names one key must
// write the whole group; every other key is its own group.
export const NOTIFICATION_SETTINGS_GROUPS: Readonly<
    Partial<Record<NotificationTypeKey, ReadonlyArray<NotificationTypeKey>>>
> = {
    missed_clock_in: ['missed_clock_in', 'timely_clock_in'],
    timely_clock_in: ['missed_clock_in', 'timely_clock_in'],
    missed_clock_out: ['missed_clock_out', 'timely_clock_out'],
    timely_clock_out: ['missed_clock_out', 'timely_clock_out'],
};

// The notification settings page, section by section, in render
// order. Only the key that owns a row appears; a sibling folded
// into the same row is reached through NOTIFICATION_SETTINGS_
// GROUPS rather than as a second checkbox.
export const NOTIFICATION_SETTINGS_SECTIONS: ReadonlyArray<{
    readonly title: NotificationSettingsSection;
    readonly area: NotificationSettingsArea;
    readonly rows: ReadonlyArray<NotificationTypeKey>;
}> = [
    {
        title: 'How Phoebe asks for help',
        area: 'How does Phoebe ask for help?',
        rows: ['agent_reminder'],
    },
    {
        title: 'Filling shifts',
        area: 'Scheduling and operations',
        rows: [
            'callout_shift_accepted',
            'outreach_finished_unfilled',
            'outreach_finished_filled',
            'callout_shift_needs_review',
        ],
    },
    {
        title: 'Shift confirmation',
        area: 'Scheduling and operations',
        rows: ['shift_confirmation_confirmed'],
    },
    {
        title: 'Arriving at shifts',
        area: 'Scheduling and operations',
        rows: [
            'missed_clock_in',
            'missed_clock_out',
            'phoebe_clocked_in_out',
            'caregiver_running_late',
            'shift_confirmation_no_response',
            'shift_confirmation_needs_review',
            'shift_confirmation_staged_messages_reminder',
            'plan_of_care_incomplete',
        ],
    },
    {
        title: 'Canceling shifts',
        area: 'Scheduling and operations',
        rows: ['shift_confirmation_declined', 'shift_cancellation'],
    },
    {
        title: 'Calls',
        area: 'Communication',
        rows: ['after_hours_receptionist_urgent', 'call_summary'],
    },
    {
        title: 'Messages',
        area: 'Communication',
        rows: [
            'inbound_sms_received',
            'conversation_assigned',
            'conversation_tagged',
        ],
    },
    {
        title: 'Activity',
        area: 'Activity',
        rows: [
            'task_assigned',
            'activity_action_required',
            'activity_urgent',
            'activity_daily_summary',
        ],
    },
];

// Which bell/top-bar category a durable Web row belongs to. The
// caller keeps its own resource-type fallback for rows whose type
// is absent, such as a legacy shift-confirmation status string.
export const NOTIFICATION_WEB_CATEGORIES: Readonly<
    Record<string, NotificationWebCategory>
> = {
    agent_reminder: 'alerts',
    callout_shift_accepted: 'outreach',
    outreach_finished_unfilled: 'outreach',
    outreach_finished_filled: 'outreach',
    callout_shift_needs_review: 'outreach',
    missed_clock_in: 'outreach',
    timely_clock_in: 'outreach',
    missed_clock_out: 'outreach',
    timely_clock_out: 'outreach',
    phoebe_clocked_in_out: 'shifts',
    caregiver_running_late: 'shifts',
    clock_in_out: 'outreach',
    shift_confirmation_no_response: 'shifts',
    shift_confirmation_needs_review: 'shifts',
    shift_confirmation_staged_messages_reminder: 'shifts',
    plan_of_care_incomplete: 'alerts',
    shift_confirmation_declined: 'shifts',
    shift_cancellation: 'shifts',
    shift_confirmation_confirmed: 'shifts',
    after_hours_receptionist_urgent: 'alerts',
    call_summary: 'alerts',
    after_hours_receptionist_digest: 'alerts',
    emergency_call_placed: 'alerts',
    inbound_sms_received: 'messages',
    conversation_assigned: 'messages',
    conversation_tagged: 'messages',
    task_assigned: 'alerts',
    activity_action_required: 'alerts',
    activity_urgent: 'alerts',
    activity_daily_summary: 'alerts',
    outreach_action: 'outreach',
    message_action: 'messages',
    shift_action: 'shifts',
    phoebe_created_outreach: 'outreach',
    phoebe_started_outreach: 'outreach',
    phoebe_monitored_outreach: 'outreach',
    phoebe_acted_on_outreach: 'outreach',
};
